/* Promo Auto-Add — keeps a configured promo item in every cart.
   Config is rendered by sections/promo-auto-add.liquid (theme editor).
   Modes:
   - size picker (default for multi-variant products): pops a dialog so the
     customer picks their size; adds the chosen variant
   - fixed variant (Variant ID override set, or single-variant product): adds silently
   State (localStorage, keyed by product so a new promo resets it):
   - axiom_promo_added_<key>     → we auto-added it during this cart cycle
   - axiom_promo_dismissed_<key> → customer removed it / closed the picker;
                                   don't re-prompt until the cart empties
   - axiom_promo_tee_seen_<key>  → last time the tee sat in this browser's cart
   - axiom_promo_order_at_<key>  → guess at the last free-tee ORDER time (cart
                                   emptied while the tee was in it = checkout);
                                   drives the cooldown for guests. Logged-in
                                   customers get the real last-order time from
                                   Liquid (cfg.lastPromoOrderAt).
   Optional discount code is auto-applied via GET /discount/<code> (sets the
   checkout cookie); automatic Buy-X-Get-Y discounts need no code.
   Flow: on load + on every cart-update event, fetch /cart.js and reconcile. */

(() => {
  if (customElements.get('promo-auto-add')) return;

  const cartRoutes = () =>
    window.routes || { cart_url: '/cart', cart_add_url: '/cart/add', cart_change_url: '/cart/change' };

  class PromoAutoAdd extends HTMLElement {
    connectedCallback() {
      if (window.Shopify && window.Shopify.designMode) return;
      try {
        this.cfg = JSON.parse(this.querySelector('script[type="application/json"]').textContent);
      } catch (e) {
        return;
      }
      if (!this.cfg || !this.cfg.enabled || !this.cfg.variantId || this.cfg.available === false) return;
      if (!this.inDateWindow()) {
        this.sweepEnded();
        return;
      }

      this.busy = false;
      this.pickerOpen = false;
      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        this.unsubscribe = subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
          if (event && event.source === 'promo-auto-add') return;
          this.scheduleCheck();
        });
      }
      if (!this.inCooldown()) this.ensureProductStrip();
      this.scheduleCheck();
    }

    disconnectedCallback() {
      if (this.unsubscribe) this.unsubscribe();
    }

    scheduleCheck() {
      clearTimeout(this.checkTimer);
      this.checkTimer = setTimeout(() => this.check(), 400);
    }

    inDateWindow() {
      const parse = (s) => {
        const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec((s || '').trim());
        return m ? new Date(+m[1], +m[2] - 1, +m[3]) : null;
      };
      const now = new Date();
      const start = parse(this.cfg.startDate);
      if (start && now < start) return false;
      const end = parse(this.cfg.endDate);
      if (end) {
        end.setDate(end.getDate() + 1); // inclusive through end of day
        if (now >= end) return false;
      }
      return true;
    }

    get stateSeed() {
      return this.cfg.productId || this.cfg.variantId;
    }
    get addedKey() {
      return `axiom_promo_added_${this.stateSeed}`;
    }
    get dismissedKey() {
      return `axiom_promo_dismissed_${this.stateSeed}`;
    }
    get teeSeenKey() {
      return `axiom_promo_tee_seen_${this.stateSeed}`;
    }
    get orderStampKey() {
      return `axiom_promo_order_at_${this.stateSeed}`;
    }
    read(key) {
      try {
        return localStorage.getItem(key);
      } catch (e) {
        return null;
      }
    }
    store(key, val) {
      try {
        if (val === null) localStorage.removeItem(key);
        else localStorage.setItem(key, val);
      } catch (e) {
        /* private mode — degrade to prompt-every-load */
      }
    }

    // Any variant of the promo product counts (customer may have picked a size manually)
    findPromoLine(cart) {
      if (this.cfg.productId) return cart.items.find((i) => i.product_id === this.cfg.productId);
      return cart.items.find((i) => i.id === this.cfg.variantId);
    }

    // Promo outside its date window (ended): take back a tee this widget auto-added while it
    // ran, so nobody checks out a full-price tee they never picked. A manually-added tee stays.
    async sweepEnded() {
      try {
        const cart = await fetch(`${cartRoutes().cart_url}.js`).then((r) => r.json());
        const line = this.findPromoLine(cart);
        if (!line || !line.properties || !line.properties._auto_added) return;
        const data = await this.cartPost(cartRoutes().cart_change_url, { id: line.key, quantity: 0 });
        this.renderCartSections(data.sections, (data.item_count || 0) === 0);
        this.publishUpdate(data);
      } catch (e) {
        if (window.console) console.warn('[promo-auto-add] ended-promo sweep failed', e);
      }
    }

    availableVariants() {
      return Array.isArray(this.cfg.variants) ? this.cfg.variants.filter((v) => v.available) : [];
    }

    // Spend threshold in cents ("free on orders over $X"); 0 = no threshold
    // (legacy "free with any other item" behavior via requireItems).
    get minSubtotal() {
      const n = parseInt(this.cfg.minSubtotal, 10);
      return Number.isFinite(n) && n > 0 ? n : 0;
    }

    // Merchandise that counts toward the threshold — everything EXCEPT the promo
    // product (the free tee is the reward, not qualifying spend).
    qualifyingSubtotal(cart) {
      return cart.items.reduce((sum, i) => {
        const isPromo = this.cfg.productId ? i.product_id === this.cfg.productId : i.id === this.cfg.variantId;
        return isPromo ? sum : sum + (i.final_line_price || 0);
      }, 0);
    }

    // Does the current cart qualify for the free tee?
    qualifies(qualifying, otherQty, min) {
      if (min > 0) return qualifying >= min;
      return this.cfg.requireItems ? otherQty > 0 : true;
    }

    // "One free tee per customer every N days" — 0/blank = off
    get cooldownMs() {
      const d = parseInt(this.cfg.cooldownDays, 10);
      return Number.isFinite(d) && d > 0 ? d * 86400000 : 0;
    }

    // Already got a free tee within the window? Logged-in truth comes from
    // Liquid (lastPromoOrderAt, epoch seconds = newest order containing the
    // promo product); guests fall back to the browser-local order stamp.
    inCooldown() {
      const ms = this.cooldownMs;
      if (!ms) return false;
      const now = Date.now();
      const liquidTs = (parseInt(this.cfg.lastPromoOrderAt, 10) || 0) * 1000;
      if (liquidTs && now - liquidTs < ms) return true;
      const localTs = parseInt(this.read(this.orderStampKey) || '0', 10);
      return !!localTs && now - localTs < ms;
    }

    // Guest-side order detection: checkout empties the cart, so a cart that
    // goes empty while the tee sat in it almost certainly became an order.
    // Stamp the last time the tee was SEEN (the order happened after that, so
    // the window can only run short, never long). Removals we perform clear
    // the seen marker in changeQty(), so they never masquerade as orders.
    trackTeePresence(cart, promoLine) {
      if (!this.cooldownMs) return;
      if (cart.item_count === 0) {
        const seen = parseInt(this.read(this.teeSeenKey) || '0', 10);
        if (seen) {
          const prev = parseInt(this.read(this.orderStampKey) || '0', 10);
          if (seen > prev) this.store(this.orderStampKey, String(seen));
          this.store(this.teeSeenKey, null);
        }
      } else {
        this.store(this.teeSeenKey, promoLine ? String(Date.now()) : null);
      }
    }

    money(cents) {
      return '$' + (cents / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }

    async check() {
      if (this.busy || this.pickerOpen) return;
      this.busy = true;
      try {
        const cart = await fetch(`${cartRoutes().cart_url}.js`).then((r) => r.json());
        const promoLine = this.findPromoLine(cart);
        this.trackTeePresence(cart, promoLine);

        if (this.inCooldown()) {
          // Free tee already claimed in the last N days — no auto-add, no promo
          // messaging, and take back a tee WE added. (A manually-added tee is
          // left alone; the automatic discount is out of the theme's reach.)
          document.querySelectorAll('.promo-cart-banner, .promo-cart-warning').forEach((el) => el.remove());
          if (cart.item_count === 0) {
            this.store(this.addedKey, null);
            this.store(this.dismissedKey, null);
          } else if (promoLine && promoLine.properties && promoLine.properties._auto_added) {
            await this.changeQty(promoLine.key, 0);
            this.store(this.addedKey, null);
          }
          return;
        }

        const otherQty = cart.item_count - (promoLine ? promoLine.quantity : 0);
        const min = this.minSubtotal;
        const qualifying = this.qualifyingSubtotal(cart);
        const qualifies = this.qualifies(qualifying, otherQty, min);
        // Remember for optimistic messaging after add/change (a fresh check() corrects it).
        this._min = min;
        this._qual = qualifying;
        this._otherQty = otherQty;
        this.ensureCartMessaging(promoLine, otherQty, qualifying, min);

        if (promoLine) {
          this.store(this.addedKey, '1');
          this.ensureDiscount();
          const autoAdded = promoLine.properties && promoLine.properties._auto_added;
          // Order no longer qualifies (dropped below the threshold / only item left) → drop the tee we added,
          // so it can't be ordered without meeting the threshold.
          if (!qualifies && autoAdded) {
            await this.changeQty(promoLine.key, 0);
            this.store(this.addedKey, null);
          }
          return;
        }

        if (cart.item_count === 0) {
          // Fresh cart (order completed or emptied) — a future qualifying order gets the promo again
          this.store(this.addedKey, null);
          this.store(this.dismissedKey, null);
          if (min > 0 || this.cfg.requireItems) return;
        } else if (this.cfg.respectRemoval && this.read(this.addedKey) === '1') {
          // We added it, it's gone, cart still has items → the customer deleted it
          this.store(this.dismissedKey, '1');
          this.store(this.addedKey, null);
        }

        if (this.read(this.dismissedKey) === '1') return;
        if (!qualifies) return; // below the threshold (or no other item) → don't add yet

        if (this.cfg.sizePrompt && this.availableVariants().length > 1) {
          this.showPicker();
        } else {
          const only = this.availableVariants();
          await this.addPromo(only.length === 1 ? only[0].id : this.cfg.variantId);
        }
      } catch (e) {
        console.warn('[promo-auto-add]', e);
      } finally {
        this.busy = false;
      }
    }

    cartSectionIds() {
      return document.querySelector('cart-drawer') ? ['cart-drawer', 'cart-icon-bubble'] : ['cart-icon-bubble'];
    }

    async cartPost(url, body) {
      body.sections = this.cartSectionIds();
      body.sections_url = window.location.pathname;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (!res.ok || data.status) throw new Error(data.description || data.message || `HTTP ${res.status}`);
      return data;
    }

    async addPromo(variantId) {
      const data = await this.cartPost(cartRoutes().cart_add_url, {
        items: [{ id: variantId, quantity: 1, properties: { _auto_added: 'true' } }],
      });
      this.store(this.addedKey, '1');
      this.renderCartSections(data.sections, false);
      this.ensureCartMessaging(true, this._otherQty || 1, this._qual || 0, this._min || 0);
      this.publishUpdate(data);
      this.ensureDiscount();
      if (this.cfg.showToast) this.showToast();
    }

    async changeQty(lineKey, quantity) {
      const data = await this.cartPost(cartRoutes().cart_change_url, { id: lineKey, quantity });
      if (quantity === 0) this.store(this.teeSeenKey, null); // our removal, not an order

      // "Now empty" means the CART is empty, not that this line hit zero — passing
      // `quantity === 0` here flagged the drawer `is-empty` (header hidden, empty-cart
      // grid layout) every time the tee was auto-removed with other items still in it.
      this.renderCartSections(data.sections, (data.item_count || 0) === 0);
      this.ensureCartMessaging(false, this._otherQty || 0, this._qual || 0, this._min || 0);
      this.publishUpdate(data);
    }

    // Cart drawer / cart page containers the promo messaging can live in.
    // The drawer also exists (hidden) on the /cart page, so the page wins.
    cartContainer() {
      return document.getElementById('main-cart-items') || document.querySelector('#CartDrawer .drawer__inner');
    }

    // One strip inside the cart, re-inserted after every re-render (check() runs on
    // each cart-update). Three states:
    //  - tee in + order qualifies                → "FREE TEE UNLOCKED" banner
    //  - tee in + order below threshold          → "not free yet" warning
    //  - tee not in + has items, below threshold → "you're $X away" progress nudge
    ensureCartMessaging(promoLine, otherQty, qualifying, min) {
      document.querySelectorAll('.promo-cart-banner, .promo-cart-warning').forEach((el) => el.remove());
      // The free-shipping nudge (free-shipping-nudge.js) owns all perk messaging in the cart
      // when it's integrated — it renders the tee as a second milestone in the same block.
      const nudge = document.querySelector('free-shipping-nudge');
      if (nudge && nudge.integrated) return;
      if (this.inCooldown()) return; // cooldown = stay quiet on every surface
      const inner = this.cartContainer();
      if (!inner) return;

      const hasThreshold = min > 0;
      const qualifies = this.qualifies(qualifying, otherQty, min);
      const remaining = hasThreshold ? Math.max(0, min - qualifying) : 0;
      const minLabel = hasThreshold ? '$' + Math.round(min / 100) : '';

      let wrap = null;
      if (promoLine && qualifies && this.cfg.showCartBanner && this.cfg.cartBannerUrl) {
        wrap = document.createElement('div');
        wrap.className = 'promo-cart-banner';
        const img = document.createElement('img');
        img.src = this.cfg.cartBannerUrl.startsWith('//') ? `https:${this.cfg.cartBannerUrl}` : this.cfg.cartBannerUrl;
        img.alt = 'Free tee unlocked - added to your order';
        img.width = 1080;
        img.height = 278;
        wrap.appendChild(img);
      } else if (promoLine && !qualifies && this.cfg.warnText !== '') {
        // Tee is in the cart but the order hasn't hit the threshold — don't let $0.00 be a surprise.
        wrap = this.buildCartNotice(
          this.cfg.warnText ||
            "Heads up — your free tee isn't free yet. It unlocks on qualifying orders and drops to $0.00 automatically.",
          hasThreshold && remaining > 0 ? `Spend ${this.money(remaining)} more to unlock $0.00 automatically.` : '',
          this.cfg.warnCta || 'Keep shopping — unlock your free tee →'
        );
      } else if (!promoLine && this.cfg.showProgress && hasThreshold && qualifying > 0 && remaining > 0) {
        // Has items but below the threshold, tee not added yet — nudge them over the line.
        wrap = this.buildCartNotice(
          `You're ${this.money(remaining)} away from a FREE Dragon Tee.`,
          `Spend ${this.money(remaining)} more (orders ${minLabel}+) and we'll add it automatically.`,
          this.cfg.warnCta || 'Keep shopping — unlock your free tee →'
        );
      }
      if (!wrap) return;
      const header = inner.querySelector('.drawer__header');
      if (header && header.parentElement === inner) header.insertAdjacentElement('afterend', wrap);
      else inner.prepend(wrap);
    }

    buildCartNotice(mainText, subText, ctaText) {
      const wrap = document.createElement('div');
      wrap.className = 'promo-cart-warning';
      wrap.setAttribute('role', 'status');
      const text = document.createElement('p');
      text.className = 'promo-cart-warning__text';
      text.textContent = subText ? `${mainText} ${subText}` : mainText;
      wrap.appendChild(text);
      if (ctaText) {
        const link = document.createElement('a');
        link.className = 'promo-cart-warning__link';
        link.href = '/collections/all';
        link.textContent = ctaText;
        wrap.appendChild(link);
      }
      return wrap;
    }

    // "FREE with every order" explainer on the promo product's own page, so the
    // full price tag can't read as a broken promise.
    ensureProductStrip() {
      if (this.cfg.showStrip === false || !this.cfg.productHandle) return;
      if (!location.pathname.split('?')[0].includes(`/products/${this.cfg.productHandle}`)) return;
      if (document.querySelector('.promo-product-strip')) return;
      const host =
        document.querySelector('product-info .product-form__buttons') ||
        document.querySelector('.product-form__buttons') ||
        document.querySelector('.product__info-container .price');
      if (!host) return;
      const strip = document.createElement('div');
      strip.className = 'promo-product-strip';
      const title = document.createElement('strong');
      title.className = 'promo-product-strip__title';
      title.textContent = this.cfg.stripTitle || 'FREE ON ORDERS OVER $299';
      const text = document.createElement('p');
      text.className = 'promo-product-strip__text';
      text.textContent =
        this.cfg.stripText ||
        'Add any other item to your cart and this tee automatically drops to $0.00. On its own it rings up at full price.';
      strip.append(title, text);
      host.insertAdjacentElement('beforebegin', strip);
    }

    publishUpdate(cartData) {
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {
          source: 'promo-auto-add',
          productVariantId: this.cfg.variantId,
          cartData,
        });
      }
    }

    // GET /discount/<code> sets Shopify's discount cookie so checkout arrives pre-applied
    ensureDiscount() {
      const code = (this.cfg.discountCode || '').trim();
      if (!code) return;
      try {
        if (sessionStorage.getItem('axiom_promo_code_applied') === code) return;
      } catch (e) {
        /* fall through — applying twice is harmless */
      }
      fetch(`/discount/${encodeURIComponent(code)}`, { credentials: 'same-origin' })
        .then(() => {
          try {
            sessionStorage.setItem('axiom_promo_code_applied', code);
          } catch (e) {}
        })
        .catch(() => {});
    }

    // Same section-patching as CartDrawer.renderContents, but WITHOUT opening the drawer.
    renderCartSections(sections, nowEmpty) {
      if (!sections) return;
      const extract = (html, sel) => {
        const el = new DOMParser().parseFromString(html, 'text/html').querySelector(sel);
        return el ? el.innerHTML : null;
      };
      const drawer = document.querySelector('cart-drawer');
      if (drawer && sections['cart-drawer']) {
        const target = document.getElementById('CartDrawer');
        const html = extract(sections['cart-drawer'], '#CartDrawer');
        if (target && html !== null) {
          target.innerHTML = html;
          drawer.classList.toggle('is-empty', !!nowEmpty);
          const overlay = drawer.querySelector('#CartDrawer-Overlay');
          if (overlay) overlay.addEventListener('click', drawer.close.bind(drawer));
        }
      }
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble && sections['cart-icon-bubble']) {
        const html = extract(sections['cart-icon-bubble'], '.shopify-section');
        if (html !== null) bubble.innerHTML = html;
      }
    }

    /* ---------- size picker dialog ---------- */

    showPicker() {
      if (this.pickerOpen || document.querySelector('.promo-picker')) return;
      this.pickerOpen = true;

      const backdrop = document.createElement('div');
      backdrop.className = 'promo-picker';

      const card = document.createElement('div');
      card.className = 'promo-picker__card';
      card.setAttribute('role', 'dialog');
      card.setAttribute('aria-modal', 'true');
      card.setAttribute('aria-label', this.cfg.promptTitle || 'Choose your size');

      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'promo-picker__close';
      close.setAttribute('aria-label', 'No thanks');
      close.innerHTML = '&times;';

      let media = null;
      if (this.cfg.imageUrl) {
        card.classList.add('promo-picker__card--media');
        media = document.createElement('div');
        media.className = 'promo-picker__media';
        const img = document.createElement('img');
        img.src = this.cfg.imageUrl.startsWith('//') ? `https:${this.cfg.imageUrl}` : this.cfg.imageUrl;
        img.alt = this.cfg.title || 'Promo item';
        img.width = 600;
        img.height = 600;
        media.appendChild(img);
      }

      const title = document.createElement('h3');
      title.className = 'promo-picker__title';
      title.textContent = this.cfg.promptTitle || 'Choose your size';

      const sub = document.createElement('p');
      sub.className = 'promo-picker__sub';
      sub.textContent = this.cfg.title || '';

      const grid = document.createElement('div');
      grid.className = 'promo-picker__grid';
      this.cfg.variants.forEach((v) => {
        const btn = document.createElement('button');
        btn.type = 'button';
        btn.className = 'promo-picker__size';
        btn.textContent = v.title;
        btn.disabled = !v.available;
        if (v.available) {
          btn.addEventListener('click', async () => {
            grid.querySelectorAll('button').forEach((b) => (b.disabled = true));
            try {
              await this.addPromo(v.id);
              this.closePicker(backdrop);
            } catch (e) {
              console.warn('[promo-auto-add]', e);
              grid.querySelectorAll('button').forEach((b) => (b.disabled = false));
              btn.disabled = !v.available;
            }
          });
        }
        grid.appendChild(btn);
      });

      const content = document.createElement('div');
      content.className = 'promo-picker__content';
      content.append(title, sub, grid);
      const noteText =
        this.cfg.pickerNote == null
          ? '100% off applies automatically in your cart. Free on orders over $299, any size.'
          : this.cfg.pickerNote;
      if (noteText) {
        const note = document.createElement('p');
        note.className = 'promo-picker__note';
        note.textContent = noteText;
        content.appendChild(note);
      }
      if (media) card.append(close, media, content);
      else card.append(close, content);
      backdrop.appendChild(card);
      document.body.appendChild(backdrop);

      const dismiss = () => {
        this.store(this.dismissedKey, '1'); // one shot per cart cycle, same as removing the item
        this.closePicker(backdrop);
      };
      close.addEventListener('click', dismiss);
      backdrop.addEventListener('click', (e) => {
        if (e.target === backdrop) dismiss();
      });
      this.escHandler = (e) => {
        if (e.key === 'Escape') dismiss();
      };
      document.addEventListener('keydown', this.escHandler);

      const first = grid.querySelector('button:not(:disabled)');
      if (first) first.focus();
    }

    closePicker(backdrop) {
      backdrop.remove();
      if (this.escHandler) document.removeEventListener('keydown', this.escHandler);
      this.escHandler = null;
      this.pickerOpen = false;
    }

    /* ---------- toast ---------- */

    showToast() {
      const prior = document.querySelector('.promo-toast');
      if (prior) prior.remove();
      const toast = document.createElement('div');
      toast.className = 'promo-toast';
      toast.setAttribute('role', 'status');
      const text = document.createElement('span');
      text.className = 'promo-toast__text';
      text.textContent = this.cfg.toastText || 'A promo item was added to your cart.';
      const close = document.createElement('button');
      close.type = 'button';
      close.className = 'promo-toast__close';
      close.setAttribute('aria-label', 'Dismiss');
      close.innerHTML = '&times;';
      close.addEventListener('click', () => toast.remove());
      toast.append(text, close);
      document.body.appendChild(toast);
      requestAnimationFrame(() => toast.classList.add('promo-toast--visible'));
      setTimeout(() => {
        toast.classList.remove('promo-toast--visible');
        setTimeout(() => toast.remove(), 400);
      }, 5000);
    }
  }

  customElements.define('promo-auto-add', PromoAutoAdd);
})();
